// v2 - with tiptap WYSIWYG

"use client"

import React, { useEffect, useRef } from 'react'
import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import { Placeholder } from '@tiptap/extension-placeholder'
import { Table, TableRow, TableHeader, TableCell } from '@tiptap/extension-table'
import { Markdown } from '@tiptap/markdown'

/*--------------------------------------------------------------------------
  WYSIWYG markdown editor (Tiptap v3). Drop-in replacement for the old
  textarea + side-preview MarkdownEditor.

  - Same props as before. `showPreview`, `textAreaRef`, `textAreaRows` and
    `parentPost` are accepted but ignored (no preview pane needed anymore).
  - Value in / value out is still a MARKDOWN STRING, so the DB format,
    ReactMarkdownMod_2 rendering, old posts and the char limit keep working.
  - `callback` is called with { target: { name, value } } exactly like
    a textarea change event, so the parent's handleChange works unchanged.

  Install (you already have the rest of Tiptap v3):
  npm i @tiptap/markdown@^3

  Note: in v3, StarterKit already bundles Link, so the separate
  @tiptap/extension-link import is no longer needed here.
--------------------------------------------------------------------------*/

const ToolbarButton = ({ onClick, label, active = false, disabled = false, title }) => (
  <button
    type="button"
    title={title}
    disabled={disabled}
    // mouseDown + preventDefault keeps the editor focused and the selection intact
    onMouseDown={(e) => {
      e.preventDefault()
      if (!disabled) onClick()
    }}
    className={`px-3 py-1 rounded border border-slate-300 text-xs font-base
      ${active
        ? 'bg-teal-500 text-white border-teal-500'
        : 'bg-white dark:bg-slate-200 text-slate-500 hover:bg-slate-100 active:bg-slate-200'}
      ${disabled ? 'opacity-40 cursor-not-allowed' : ''}`}
  >
    {label}
  </button>
)

const MarkdownEditor = ({
  textAreaName,
  textAreaValue,
  textAreaPlaceholder,
  callback,
  isTimelessPost = false,
  onKeyDown,
  // accepted for backwards compatibility, unused:
  showPreview,
  textAreaRef,
  textAreaId,
  textAreaOnChange,
  textAreaRows,
  parentPost = null,
}) => {
  // The last markdown string we emitted to the parent. Used to tell apart
  // "parent changed the value" (edit mode load, reset after submit)
  // from "the value is just echoing what the user typed".
  const lastEmittedRef = useRef(textAreaValue || '')

  const editor = useEditor({
    immediatelyRender: false, // avoids Next.js hydration mismatch
    shouldRerenderOnTransaction: true, // keeps toolbar active states in sync
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] }, // matches the H2 / H3 toolbar + shiftHeading in the renderer
        underline: false,            // markdown has no underline
        link: {
          openOnClick: false,
          autolink: true,
          HTMLAttributes: { rel: 'noopener noreferrer', target: '_blank' },
        },
      }),
      Placeholder.configure({
        placeholder: textAreaPlaceholder || 'Write something…',
      }),
      Table.configure({ resizable: false }),
      TableRow,
      TableHeader,
      TableCell,
      Markdown,
    ],
    content: textAreaValue || '',
    contentType: 'markdown', // initial content is parsed as markdown
    editorProps: {
      attributes: {
        // Must be ONE line of single-space-separated classes: Tiptap adds these via
        // classList.add, which throws on newlines/tabs. Hence the join(' ').
        class: [
          'md-editor-content prose max-w-none dark:prose-invert focus:outline-none',
          'min-h-[8rem] max-h-[calc(100vh-24rem)] overflow-y-auto', //  was max-h-[70vh]
          '[&_table]:w-full [&_table]:text-sm [&_table]:border-collapse',
          '[&_th]:border [&_th]:border-slate-200 dark:[&_th]:border-slate-600 [&_th]:px-3 [&_th]:py-2 [&_th]:text-left [&_th]:font-semibold [&_th]:bg-slate-100 dark:[&_th]:bg-slate-700',
          '[&_td]:border [&_td]:border-slate-200 dark:[&_td]:border-slate-600 [&_td]:px-3 [&_td]:py-2',
          '[&_.selectedCell]:bg-teal-100 dark:[&_.selectedCell]:bg-teal-900',
          '[&_a]:text-teal-500',
        ].join(' '),
      },
      handleKeyDown: (_view, event) => {
        onKeyDown?.(event) // keep the parent's @-mention tracking working
        return false       // never swallow the event
      },
    },
    onUpdate: ({ editor }) => {
      const markdown = editor.getMarkdown()
      lastEmittedRef.current = markdown
      callback?.({ target: { name: textAreaName, value: markdown } })
    },
  })

  // Parent -> editor sync: edit mode loading a post, or reset to "" after submit.
  useEffect(() => {
    if (!editor) return
    const incoming = textAreaValue || ''
    if (incoming !== lastEmittedRef.current) {
      lastEmittedRef.current = incoming
      editor.commands.setContent(incoming, { contentType: 'markdown', emitUpdate: false })
    }
  }, [textAreaValue, editor])

  if (!editor) return null

  const setLink = () => {
    const previous = editor.getAttributes('link').href || ''
    const url = window.prompt('URL', previous)
    if (url === null) return // cancelled
    if (url.trim() === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run()
      return
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url.trim() }).run()
  }

  const inTable = editor.isActive('table')

  return (
    <div className="w-full mx-auto">
      {/* placeholder styling (Tiptap's Placeholder extension only adds a class + data attribute) */}
      <style>{`
        .md-editor-content p.is-editor-empty:first-child::before {
          content: attr(data-placeholder);
          float: left;
          height: 0;
          pointer-events: none;
          color: #94a3b8;
        }
      `}</style>

      {/* toolbar */}
      <div className="mb-2 rounded-md bg-slate-50 dark:bg-slate-700 p-2 flex flex-wrap gap-2">
        <ToolbarButton title="Bold" label={<b>B</b>} active={editor.isActive('bold')}
          onClick={() => editor.chain().focus().toggleBold().run()} />
        <ToolbarButton title="Italic" label={<i>I</i>} active={editor.isActive('italic')}
          onClick={() => editor.chain().focus().toggleItalic().run()} />
        <ToolbarButton title="Heading 2" label="H2" active={editor.isActive('heading', { level: 2 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} />
        <ToolbarButton title="Heading 3" label="H3" active={editor.isActive('heading', { level: 3 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} />
        <ToolbarButton title="Bullet list" label="• List" active={editor.isActive('bulletList')}
          onClick={() => editor.chain().focus().toggleBulletList().run()} />
        <ToolbarButton title="Numbered list" label="1. List" active={editor.isActive('orderedList')}
          onClick={() => editor.chain().focus().toggleOrderedList().run()} />
        <ToolbarButton title="Link" label="Url" active={editor.isActive('link')} onClick={setLink} />
        <ToolbarButton title="Quote" label={`"Quote"`} active={editor.isActive('blockquote')}
          onClick={() => editor.chain().focus().toggleBlockquote().run()} />
        <ToolbarButton title="Inline code" label="<Code/>" active={editor.isActive('code')}
          onClick={() => editor.chain().focus().toggleCode().run()} />
        <ToolbarButton title="Insert table" label="Table" active={inTable}
          onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()} />

        {/* table controls, only while the cursor is inside a table */}
        {inTable && (
          <>
            <span className="w-px bg-slate-300 dark:bg-slate-500 mx-1" />
            <ToolbarButton title="Add row below" label="+ Row"
              onClick={() => editor.chain().focus().addRowAfter().run()} />
            <ToolbarButton title="Add column right" label="+ Col"
              onClick={() => editor.chain().focus().addColumnAfter().run()} />
            <ToolbarButton title="Delete row" label="− Row"
              onClick={() => editor.chain().focus().deleteRow().run()} />
            <ToolbarButton title="Delete column" label="− Col"
              onClick={() => editor.chain().focus().deleteColumn().run()} />
            <ToolbarButton title="Delete table" label="Delete table"
              onClick={() => editor.chain().focus().deleteTable().run()} />
          </>
        )}
      </div>

      {/* editing surface: what you see here is what gets published */}
      <div
        className={`w-full p-4 ${isTimelessPost ? 'pt-4 pb-4' : 'pt-12 pb-14'}
          text-base font-inter ${parentPost ? 'rounded' : 'rounded-xl'}
          bg-slate-100 text-black
          dark:bg-slate-800 dark:text-white dark:border dark:border-slate-700`}
        onClick={() => editor.chain().focus().run()}
      >
        <EditorContent editor={editor} />
      </div>
    </div>
  )
}

export default MarkdownEditor




// v1 - with preview on the side 

// import React, { useState, useRef } from 'react';
// import ReactMarkdown from 'react-markdown';
// import ReactMarkdownMod from '../Atoms/ReactMarkdownMod';
// import ReactMarkdownMod_2 from '../Atoms/ReactMarkdownMod_2';

// const MarkdownEditor = ({showPreview, textAreaRef, textAreaName, textAreaId, textAreaValue, textAreaOnChange, textAreaPlaceholder,  callback, textAreaRows, parentPost=null, isTimelessPost=false, onKeyDown}) => {
//   const [text, setText] = useState('');
// //   const textAreaRef = useRef(null

// const [isAtPressed, setIsAtPressed] = useState(false);

//   const [currentWord, setCurrentWord] = useState("");

//   // const handleKeyDown = (e) => {
//   //   if (e.key === "@") {
//   //     // Mark that '@' is pressed
//   //     setIsAtPressed(true);
//   //     setCurrentWord(""); // Reset current word when '@' is pressed
//   //   } else if (isAtPressed && /^[a-zA-Z]$/.test(e.key)) {
//   //     // If '@' was pressed and the current key is a letter, add the letter to the current word
//   //     setCurrentWord((prev) => prev + e.key);
//   //   } else if (e.key === " " || e.key === "Enter" || e.key === "Backspace") {
//   //     // When space, enter, or backspace is pressed, reset
//   //     if (isAtPressed && currentWord) {
//   //       console.log(`Word after @: ${currentWord}`);
//   //     }
//   //     setIsAtPressed(false);
//   //     setCurrentWord("");
//   //   }
//   // };

//   const insertFormatting = (startChars, endChars = startChars) => {
//     const textarea = textAreaRef.current;
//     const start = textarea.selectionStart;
//     const end = textarea.selectionEnd;
//     const selectedText = text.trim().substring(start, end);
    

//     if (selectedText) {
//       const beforeText = text.substring(0, start);
//       const afterText = text.substring(end);
//       const newText = `${beforeText}${startChars}${selectedText}${endChars}${afterText}`;
//       setText(newText);

//       if (callback) {
//         callback({
//           target: {
//             name: textAreaName,
//             value: newText,
//           },
//         });
//       }
//     }
//   };

//   const insertPrefix = (prefix) => {
//     const textarea = textAreaRef.current;
//     const start = textarea.selectionStart;
//     const end = textarea.selectionEnd;
//     const selectedText = text.substring(start, end);
//     const selectedLines = selectedText.split('\n');
    
//     const newLines = selectedLines.map(line => 
//       line.trim() ? `${prefix}${line}` : line
//     );
    
//     const beforeText = text.substring(0, start);
//     const afterText = text.substring(end);
//     const newText = `${beforeText}${newLines.join('\n')}${afterText}`;
//     setText(newText);

//     if (callback) {
//       callback({
//         target: {
//           name: textAreaName,
//           value: newText,
//         },
//       });
//     }
    
//   };


//   const insertTable = () => {
//   const textarea = textAreaRef.current;

//   if (!textarea) return;

//   const start = textarea.selectionStart;
//   const end = textarea.selectionEnd;

//   const table = `| Header 1 | Header 2 | Header 3 |
// | --- | --- | --- |
// | Cell | Cell | Cell |
// | Cell | Cell | Cell |`;

//   const beforeText = text.substring(0, start);
//   const afterText = text.substring(end);

//   // Add newlines when inserting into existing content
//   const prefix = beforeText && !beforeText.endsWith('\n') ? '\n\n' : '';
//   const suffix = afterText && !afterText.startsWith('\n') ? '\n\n' : '';

//   const newText = `${beforeText}${prefix}${table}${suffix}${afterText}`;

//   setText(newText);

//   if (callback) {
//     callback({
//       target: {
//         name: textAreaName,
//         value: newText,
//       },
//     });
//   }

//   // Put cursor back into the textarea
//   setTimeout(() => {
//     textarea.focus();

//     // Put cursor inside the first header
//     const cursorPosition =
//       start +
//       prefix.length +
//       2; // after "| "

//     textarea.setSelectionRange(cursorPosition, cursorPosition + 8);
//   }, 0);
// };

//   const handleLink = () => {
//     const textarea = textAreaRef.current;
//     const start = textarea.selectionStart;
//     const end = textarea.selectionEnd;
//     const selectedText = text.substring(start, end);
    
//     if (selectedText) {
//       const beforeText = text.substring(0, start);
//       const afterText = text.substring(end);
//       const newText = `${beforeText}[${selectedText}](url)${afterText}`;
//       setText(newText);
//     }
//   };

//   const ToolbarButton = ({ onClick, label }) => (
//     <button
//       onClick={onClick}
//       className="px-3 py-1 rounded bg-white dark:bg-slate-200 border border-slate-300 hover:bg-slate-100 active:bg-slate-200 text-slate-500 text-xs font-base"
//     >
//       {label}
//     </button>
//   );

//   return (
//     <div className="w-full mx-auto">
//       <div className="mb-2 rounded-md bg-slate-50 dark:bg-slate-700 p-2 flex flex-wrap gap-2">
//         <ToolbarButton onClick={(x) => {return insertFormatting('**');}} label={<><b>B</b></>} />
//         <ToolbarButton onClick={() => insertFormatting('*')} label={<><i>I</i></>} />
//         {/* <ToolbarButton onClick={() => insertFormatting('# ')} label="H1" />
//         <ToolbarButton onClick={() => insertFormatting('## ')} label="H2" /> */}
//           <ToolbarButton onClick={() => insertPrefix('## ')} label="H2" />
//           <ToolbarButton onClick={() => insertPrefix('### ')} label="H3" />
//         <ToolbarButton onClick={() => insertPrefix('- ')} label="• List" />
//         {/* <ToolbarButton onClick={() => insertPrefix('1. ')} label="1. List" /> */}
//         <ToolbarButton onClick={() => handleLink()} label="Url" />
//         <ToolbarButton onClick={() => insertPrefix('> ')} label={`"Quote"`} />
//         <ToolbarButton onClick={() => insertFormatting('`')} label="<Code/>" />
//           <ToolbarButton onClick={insertTable} label="Table"/>
//       </div>
      
//       <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
//         <div className={showPreview ? `col-span-2` : 'col-span-3'}>
//           <textarea
//           onKeyDown={onKeyDown}
//           onClick={()=>{setText(textAreaValue)}}
//           rows={textAreaRows || 1}
//           style={{resize: 'none'}}
//           // onKeyDown={handleKeyDown}
//           name={textAreaName}
//           id={textAreaId}
//             ref={textAreaRef}
//             value={text || textAreaValue}
//             onChange={(e) => {setText(e.target.value); callback(e); }}
//           //   className="w-full h-96 p-4 border rounded-lg font-mono resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 text-slate-700"
//           // className='w-full dark:bg-inherit mb-4 appearance-none border border-slate-400 dark:border-slate-600 rounded-lg py-2 px-3 leading-tight focus:outline-none focus:shadow-outline text-sm'
//               className={`

//       w-full
//       p-4
//      ${isTimelessPost ? 'pt-4 pb-4' : 'pt-12 pb-14'}
//       text-base
//       font-inter
//       ${parentPost ? 'rounded' : 'rounded-xl'}
//       bg-slate-100
//       text-black
//       dark:bg-slate-800
//       dark:text-white
//       dark:border
//       dark:border-slate-700
//       focus:outline-none
//       dark:placeholder-slate-400
//     `}
//             placeholder={textAreaPlaceholder || "Write your markdown here..."}
//           />
//         </div>
        
//         {showPreview && <div className="hidden sm:block sm:col-span-1 border border-slate-100 rounded-xl p-4 prose max-w-none h-full max-h-[634px] overflow-y-scroll bg-white dark:bg-slate-800 dark:border-slate-700 dark:prose-invert">
//           <ReactMarkdownMod_2>{text}</ReactMarkdownMod_2>
//           {/* <ReactMarkdownMod args={text}/> */}
//         </div>}
//       </div>
//     </div>
//   );
// };

// export default MarkdownEditor;
